import { useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Download, RotateCcw } from 'lucide-react';
import {
  GradeSubmissionPayload,
  LmsAssignment,
  LmsSubmissionRow,
  LmsSubmissionStatus,
  LmsSubmissionsApi,
  downloadSubmissionFile,
} from '@/services/lms.api';
import { DataTable } from '@/components/ui/DataTable';
import { Pagination } from '@/components/ui/Pagination';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Textarea } from '@/components/ui/Input';
import { formatBytes, formatDateTime } from '@/lib/format';
import { toast } from '@/stores/toast.store';

const statusTone: Record<LmsSubmissionStatus, 'blue' | 'green' | 'amber'> = {
  submitted: 'blue',
  graded: 'green',
  returned: 'amber',
};

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

export function LmsSubmissionsModal({
  assignment,
  onClose,
}: {
  assignment: LmsAssignment;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['lms-submissions', assignment.id, page, limit],
    queryFn: () => LmsSubmissionsApi.list(assignment.id, { page, limit }),
    placeholderData: keepPreviousData,
  });

  const rows = pageData?.items ?? [];
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['lms-submissions', assignment.id] });

  return (
    <Modal
      open
      onClose={onClose}
      title={selected ? `Submission — ${selected.studentName ?? 'Unknown student'}` : `Submissions — ${assignment.title}`}
      size="xl"
    >
      {selected ? (
        <SubmissionDetail
          assignment={assignment}
          submission={selected}
          onBack={() => setSelectedId(null)}
          onChanged={invalidate}
        />
      ) : (
        <div>
          <DataTable<LmsSubmissionRow>
            rows={rows}
            getRowId={(r) => r.id}
            isLoading={isLoading}
            emptyMessage="No submissions yet."
            columns={[
              {
                key: 'student',
                header: 'Student',
                render: (r) => (
                  <div>
                    <div className="font-medium text-slate-900">{r.studentName ?? '—'}</div>
                    <div className="text-xs text-slate-400">{r.admissionNumber ?? '—'}</div>
                  </div>
                ),
              },
              { key: 'submittedAt', header: 'Submitted', render: (r) => formatDateTime(r.submittedAt) },
              {
                key: 'isLate',
                header: 'Late',
                render: (r) => (r.isLate ? <Badge tone="red">Late</Badge> : <span className="text-slate-300">—</span>),
              },
              { key: 'status', header: 'Status', render: (r) => <Badge tone={statusTone[r.status]}>{r.status}</Badge> },
              {
                key: 'marks',
                header: 'Marks',
                render: (r) =>
                  r.status === 'graded' || r.status === 'returned'
                    ? r.marks != null
                      ? `${r.marks}/${assignment.maxMarks ?? '—'}`
                      : '—'
                    : '—',
              },
            ]}
            actions={(r) => (
              <button className="btn-secondary" onClick={() => setSelectedId(r.id)}>
                View
              </button>
            )}
          />

          {pageData && (
            <Pagination
              page={pageData.page}
              totalPages={pageData.totalPages}
              total={pageData.total}
              limit={pageData.limit}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setPage(1);
                setLimit(l);
              }}
            />
          )}
        </div>
      )}
    </Modal>
  );
}

function SubmissionDetail({
  assignment,
  submission,
  onBack,
  onChanged,
}: {
  assignment: LmsAssignment;
  submission: LmsSubmissionRow;
  onBack: () => void;
  onChanged: () => void;
}) {
  const [downloading, setDownloading] = useState<Set<number>>(new Set());
  const [confirmReturn, setConfirmReturn] = useState(false);
  const [returnFeedback, setReturnFeedback] = useState(submission.feedback ?? '');

  const handleDownload = async (index: number, name: string) => {
    setDownloading((s) => new Set(s).add(index));
    try {
      await downloadSubmissionFile(submission.id, index, name);
    } catch (e) {
      toast.error(errMsg(e) ?? 'Download failed');
    } finally {
      setDownloading((s) => {
        const next = new Set(s);
        next.delete(index);
        return next;
      });
    }
  };

  const returnMutation = useMutation({
    mutationFn: () => LmsSubmissionsApi.returnForRevision(submission.id, { feedback: returnFeedback }),
    onSuccess: () => {
      onChanged();
      setConfirmReturn(false);
      toast.success('Returned for revision');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Return failed'),
  });

  return (
    <div className="space-y-5">
      <button
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
        onClick={onBack}
      >
        <ArrowLeft className="h-4 w-4" /> Back to submissions
      </button>

      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={statusTone[submission.status]}>{submission.status}</Badge>
        {submission.isLate && <Badge tone="red">Late</Badge>}
        <span className="text-xs text-slate-400">Submitted {formatDateTime(submission.submittedAt)}</span>
      </div>

      {submission.textBody && (
        <div>
          <h3 className="mb-1 text-sm font-medium text-slate-700">Text response</h3>
          <div className="whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
            {submission.textBody}
          </div>
        </div>
      )}

      {submission.files.length > 0 && (
        <div>
          <h3 className="mb-1 text-sm font-medium text-slate-700">Files</h3>
          <ul className="divide-y divide-slate-200 rounded-md border border-slate-200">
            {submission.files.map((f, i) => (
              <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                <div>
                  <div className="text-slate-800">{f.name}</div>
                  <div className="text-xs text-slate-400">{formatBytes(f.size)}</div>
                </div>
                <button
                  className="btn-secondary"
                  onClick={() => handleDownload(i, f.name)}
                  disabled={downloading.has(i)}
                >
                  <Download className="mr-1.5 h-4 w-4" />
                  {downloading.has(i) ? 'Downloading…' : 'Download'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 p-4">
        <h3 className="mb-3 text-sm font-medium text-slate-700">
          {submission.status === 'graded' || submission.status === 'returned' ? 'Regrade' : 'Grade'}
        </h3>
        <GradeForm assignment={assignment} submission={submission} onGraded={onChanged} />
      </div>

      {submission.status !== 'returned' && (
        <div className="rounded-lg border border-slate-200 p-4">
          <h3 className="mb-3 text-sm font-medium text-slate-700">Return for revision</h3>
          <Field label="Feedback" required hint="Required — tells the student what to fix before resubmitting.">
            <Textarea
              rows={3}
              value={returnFeedback}
              onChange={(e) => setReturnFeedback(e.target.value)}
              placeholder="What needs to change…"
            />
          </Field>
          <button
            className="btn-secondary mt-3"
            onClick={() => setConfirmReturn(true)}
            disabled={!returnFeedback.trim()}
          >
            <RotateCcw className="mr-1.5 h-4 w-4" /> Return for revision
          </button>
        </div>
      )}

      <ConfirmDialog
        open={confirmReturn}
        onClose={() => setConfirmReturn(false)}
        onConfirm={() => returnMutation.mutate()}
        loading={returnMutation.isPending}
        title="Return submission for revision?"
        message="The student will see this submission as returned and can resubmit."
        confirmText="Return"
        destructive={false}
      />
    </div>
  );
}

function GradeForm({
  assignment,
  submission,
  onGraded,
}: {
  assignment: LmsAssignment;
  submission: LmsSubmissionRow;
  onGraded: () => void;
}) {
  const feedbackOnly = assignment.maxMarks == null;

  const schema = useMemo(
    () =>
      z.object({
        marks: feedbackOnly
          ? z.string().optional()
          : z
              .string()
              .min(1, 'Required')
              .refine(
                (v) => {
                  const n = Number(v);
                  return Number.isFinite(n) && n >= 0 && n <= (assignment.maxMarks as number);
                },
                { message: `Must be between 0 and ${assignment.maxMarks}` },
              ),
        feedback: z.string().optional().or(z.literal('')),
      }),
    [feedbackOnly, assignment.maxMarks],
  );
  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      marks: submission.marks != null ? String(submission.marks) : '',
      feedback: submission.feedback ?? '',
    },
  });

  const grade = useMutation({
    mutationFn: (v: FormValues) => {
      const payload: GradeSubmissionPayload = {
        marks: feedbackOnly ? undefined : Number(v.marks),
        feedback: v.feedback?.trim() ? v.feedback : undefined,
      };
      return LmsSubmissionsApi.grade(submission.id, payload);
    },
    onSuccess: () => {
      onGraded();
      toast.success('Grade saved');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Grading failed'),
  });

  return (
    <form className="space-y-3" onSubmit={handleSubmit((v) => grade.mutate(v))}>
      {!feedbackOnly && (
        <Field label="Marks" required error={errors.marks?.message} hint={`Out of ${assignment.maxMarks}`}>
          <Input type="number" min={0} max={assignment.maxMarks ?? undefined} {...register('marks')} />
        </Field>
      )}
      {feedbackOnly && (
        <p className="text-xs text-slate-400">
          This assignment has no max marks — feedback only, no numeric grade.
        </p>
      )}
      <Field label="Feedback" hint="Optional">
        <Textarea rows={3} {...register('feedback')} placeholder="Feedback for the student…" />
      </Field>
      <button type="submit" className="btn-primary" disabled={grade.isPending}>
        {grade.isPending ? 'Saving…' : 'Save grade'}
      </button>
    </form>
  );
}
